import { isDuelLevel, type DuelLevel } from '../battle/aiSkill';
import { validateGameDefinition, type GameDefinition } from '../game/definition';
import { MAX_SEATS } from '../battle/arenaDef';
import type { FighterId, FighterInput } from '../battle/types';
import type {
  BattleResult,
  CardRole,
  FactionId,
  Move,
  PieceId,
  SupportCardId,
} from '../game/types';
import { CARD_CHOICE_SECONDS, CARD_REVEAL_SECONDS, type CardScore } from '../game/cardBattle';
import { isValidJoinCode } from '../ids/joinCode';
import type { BoardDef } from '../board/types';

/**
 * The wire protocol between the game client and the room server. Both sides import these types
 * and the validators, so a client and a server built from the same commit always agree; a
 * mismatch is caught by `PROTOCOL_VERSION` in the server's greeting.
 *
 * The server is authoritative: clients send intentions (a move, a fighter's input) and apply only
 * what the server echoes back, in the order it was sent.
 */
// v3: clients must understand complete game definitions, custom rosters and per-cell arenas.
// A v2 client would otherwise join an editor game using the wrong board/rules.
// v5: tick bundles carry the human-friendly AI movement modifier. Older clients
// would silently discard it and diverge from the authoritative battle simulation.
// v6: a duel's start names the level each arena-AI side is scaled to. Older clients built those
// fighters unscaled and diverged from the server from the first blow.
// v7: a dropped seat goes to an AI stand-in that its person takes back (`reclaim`). An older
// client would read the stand-in as its seat lost for good and watch for the rest of the game.
// v8: the rules both sides play changed (audit step 7): Curse slows a fighter, holds last five
// turns, a neutral side is lit and can be imprisoned, a seat out of time passes. An older client
// would play a different game from the server's and fall out of step with it.
// v9: the turn walk changed (audit step 8): Shift Time frees the prisoners of either side, the
// turn comes round to a side that can act, and rounds pass while every side is held. An older
// client would disagree with the server over whose turn it is.
// v10: the duel changed (audit step 9): only a blow staggers, fighters stop against each other, a
// shot fired point-blank into a wall breaks there, walls stop auras and bursts, and a shapeshifter
// steers a copied bolt. An older client would fight a different duel from the server's.
// v11: a Card reveal waits online up to 30 s for its people, not 8 (audit step 18b); an older
// client refuses the longer wait as out of range.
// v12: the duel changed (audit step 23b): blows and bursts are wound up and land a quarter of a
// second after the press, a lunge beyond their range. An older client would fight another duel.
// v13: a player may offer a draw (audit step 29b), and a game can end in the `draw` move the table
// plays once every side has accepted one. An older client would not know the move, and would lose
// track of the game.
export const PROTOCOL_VERSION = 13;
export const MAX_ROOM_MEMBERS = 6;
export const MAX_NAME_LENGTH = 24;
/** Seconds per move the host may give the chess clock; 0 means no clock. */
export const CLOCK_CHOICES: readonly number[] = [0, 30, 60, 120, 300];
/** The seat of a member who only watches. */
export const SPECTATOR_SEAT = -1;
const MAX_ID_LENGTH = 64;
const MAX_TOKEN_LENGTH = 128;

/** Who plays a seat once the game has started: a person (at some client) or the server's AI. */
/** Who plays a seat once the game starts: a person, the AI, or nobody (the side sits out). */
export type SeatKind = 'human' | 'ai' | 'none';

/**
 * The sides that play a room's game, by faction id in board order: every side whose seat is not
 * 'none'. The server starts the game with them, and a client builds the same game from the seats
 * where nothing else says which: a replay, whose record has only the seats.
 */
export function playingFromSeats(board: BoardDef, seats: readonly SeatKind[]): FactionId[] {
  return board.factions.filter((_, i) => seats[i] !== 'none').map((f) => f.id);
}

/** A seat as the host set it up: open for a person, the AI's (with its levels), or sitting out. */
export interface RoomSeat {
  kind: 'open' | 'ai' | 'none';
  /** Plies the board AI looks ahead, 1 … 4. */
  depth?: number;
  duel?: DuelLevel;
}

/** How the room settles captures: people fight in the arena, the AI fights every duel, or a roll. */
export type RoomFights = 'arena' | 'card' | 'roll' | 'watch';

export interface LobbyMember {
  /** Faction index on the board, or `SPECTATOR_SEAT` for someone who only watches. */
  seat: number;
  name: string;
  host: boolean;
  connected: boolean;
}

export interface LobbySetup {
  definition?: GameDefinition;
  boardId: string;
  /** How many of the board's factions play; seats nobody takes are filled by the AI at start. */
  players: number;
  /** The chess clock: seconds a person has per move, 0 for none. Duels are never clocked. */
  clockSeconds: number;
  /** One entry per side of the board; absent for rooms made by an older client (first `players` open). */
  seats?: RoomSeat[];
  fights?: RoomFights;
  humanFriendlyAi?: boolean;
  engagement?: 'immediate' | 'muster';
}

export interface Lobby {
  code: string;
  setup: LobbySetup;
  members: LobbyMember[];
  started: boolean;
}

/** One step of a game as the server recorded it, enough to rebuild the state after a reload. */
export type HistoryEntry =
  | { kind: 'move'; move: Move; by: number }
  | { kind: 'battle'; result: BattleResult; rng: number; card?: CardScore[] };

export type TickInputs = Partial<Record<FighterId, FighterInput>>;

/** The inputs of one simulation step of a duel; a fighter missing from `inputs` stands still. */
export interface TickBundle {
  tick: number;
  inputs: TickInputs;
  /** A fighter that gave up on this tick, before the step. */
  forfeit?: FighterId;
}

export interface BattleStart {
  seed: number;
  attackerId: PieceId;
  defenderId: PieceId;
  /** Which sides a person fights; the server's arena AI drives the others. */
  humans: Record<FighterId, boolean>;
  /**
   * The duel level each side the arena AI fought from the start is scaled to; a person's side is
   * not scaled. Fixed at the start: a side handed to the AI later keeps the stats it began with.
   */
  levels?: Partial<Record<FighterId, DuelLevel>>;
}

/** A finished game as the server lists it. */
export interface ReplaySummary {
  id: string;
  /** When the game ended, milliseconds since the epoch. */
  finishedAt: number;
  setup: LobbySetup;
  seats: SeatKind[];
  names: string[];
  /** Moves played and duels fought. */
  moves: number;
  duels: number;
  winner: FactionId | 'draw' | null;
}

/** A duel of a finished game: enough to step it again exactly as it was fought. */
export interface ReplayDuel extends BattleStart {
  /** Index in the history of the battle entry this duel settled. */
  index: number;
  ticks: TickBundle[];
}

/** Everything needed to play a finished game back move by move and duel by duel. */
export interface Replay {
  summary: ReplaySummary;
  seed: number;
  history: HistoryEntry[];
  duels: ReplayDuel[];
}

export type ClientMessage =
  | {
      t: 'create';
      definition?: GameDefinition;
      boardId: string;
      players: number;
      seat: number;
      name: string;
      clockSeconds: number;
      seats?: RoomSeat[];
      fights?: RoomFights;
      humanFriendlyAi?: boolean;
      engagement?: 'immediate' | 'muster';
    }
  /** `watch` asks for no seat; without it a seat is taken when one is free, else the newcomer watches. */
  | { t: 'join'; code: string; name: string; watch?: boolean }
  | { t: 'leave' }
  | { t: 'start' }
  | { t: 'move'; move: Move }
  | { t: 'cardChoice'; cardId: SupportCardId | null; role?: CardRole }
  /** The person has read the revealed cards; the clash may be settled. */
  | { t: 'cardContinue' }
  | { t: 'input'; tick: number; input: FighterInput }
  | { t: 'forfeit' }
  /**
   * `standIn` says the person was already told the AI took their seat: it keeps (or takes) the
   * seat until they `reclaim` it, so nobody is put back into the game before they are ready.
   */
  | { t: 'resume'; token: string; standIn?: boolean }
  /** Takes the seat back from the AI standing in for this person. */
  | { t: 'reclaim' }
  /** Offers the other sides a draw: on the person's own turn, once a turn (D-46). */
  | { t: 'offerDraw' }
  /** Answers a draw offered to this person's side. */
  | { t: 'answerDraw'; accept: boolean }
  | { t: 'ping'; sent: number }
  | { t: 'replays' }
  | { t: 'replay'; id: string };

export type ErrorCode =
  | 'bad-message'
  | 'unsupported'
  | 'no-room'
  | 'room-full'
  | 'already-started'
  | 'not-started'
  | 'not-host'
  | 'not-in-room'
  | 'not-your-turn'
  | 'illegal-move'
  | 'bad-setup'
  | 'bad-token'
  | 'expired'
  | 'too-many-attempts'
  | 'no-battle'
  | 'spectator'
  | 'no-replay'
  | 'no-draw';

export type EndReason = 'finished' | 'abandoned' | 'expired';

export type ServerMessage =
  | {
      t: 'hello';
      version: string;
      protocol: number;
      serverTime: number;
      /**
       * The server's content (`contentDigest` in `@archon3d/content`): a client built from other
       * content would replay the same history into another game, so it asks to be updated.
       */
      content?: string;
    }
  | { t: 'pong'; sent: number; serverTime: number }
  | { t: 'created'; code: string; seat: number; token: string; lobby: Lobby }
  | { t: 'joined'; code: string; seat: number; token: string; lobby: Lobby }
  | { t: 'lobby'; lobby: Lobby }
  | {
      t: 'started';
      seed: number;
      setup: LobbySetup;
      seats: SeatKind[];
      names: string[];
      /** The factions that play, by id; absent from an older server (the first `players`). */
      playing?: string[];
    }
  /**
   * After `started` on a resume: everything that happened so far, the duel if one runs, the seats
   * the AI stands in for (a returning person's own among them waits for their OK), and the seats
   * whose people dropped with the seconds left before the AI stands in for them.
   */
  | {
      t: 'sync';
      history: HistoryEntry[];
      battle?: BattleStart & { ticks: TickBundle[] };
      standIns?: number[];
      paused?: { seat: number; secondsLeft: number }[];
    }
  /** `timeout` marks a move the server played because the seat's clock ran out. */
  | { t: 'moved'; move: Move; by: number; timeout?: boolean }
  /** The chess clock of the seat to move, once a second while it runs. */
  | { t: 'clock'; seat: number; secondsLeft: number }
  | ({ t: 'battle' } & BattleStart)
  | ({ t: 'tick' } & TickBundle)
  | { t: 'battleEnded'; result: BattleResult; rng: number }
  | {
      t: 'cardPrompt';
      faction: FactionId;
      participants: {
        pieceId: PieceId;
        faction: FactionId;
        character: string;
        name: string;
        main: number;
        level?: DuelLevel;
      }[];
      hand: { id: SupportCardId; name: string; role: CardRole; water: boolean; fit: string }[];
      /** Whole seconds left to choose: fewer than the full time for a person back mid-choice. */
      secondsLeft?: number;
    }
  /**
   * `holdSeconds`: the clash waits for the people in it to continue, at most this long; absent
   * when no person took part and the outcome follows in a beat.
   */
  | { t: 'cardReveal'; scores: CardScore[]; holdSeconds?: number }
  /** A seat's person dropped: the AI stands in for them if they are not back in `secondsLeft`. */
  | { t: 'paused'; seat: number; secondsLeft: number }
  | { t: 'resumed'; seat: number }
  /**
   * Who plays a seat now. `ai` with `standIn` is the AI playing for a person who may take the seat
   * back; `ai` alone is the seat gone to the AI for good; `human` is the person back in it.
   */
  | { t: 'seatChanged'; seat: number; kind: SeatKind; standIn?: boolean }
  /**
   * `seat` offers a draw, and the seats in `waiting` have yet to answer it; sent again as each
   * accepts. Once none is left the table plays the `draw` move.
   */
  | { t: 'drawOffer'; seat: number; waiting: number[] }
  /** The offer is off: declined by the seat `by`, or, with `by` null, lapsed as its turn ended. */
  | { t: 'drawDeclined'; by: number | null }
  | { t: 'ended'; reason: EndReason; winner: FactionId | 'draw' | null }
  | { t: 'replays'; replays: ReplaySummary[] }
  | { t: 'replay'; replay: Replay }
  | { t: 'error'; code: ErrorCode; message: string };

const ERROR_CODES: readonly ErrorCode[] = [
  'bad-message',
  'unsupported',
  'no-room',
  'room-full',
  'already-started',
  'not-started',
  'not-host',
  'not-in-room',
  'not-your-turn',
  'illegal-move',
  'bad-setup',
  'bad-token',
  'expired',
  'too-many-attempts',
  'no-battle',
  'spectator',
  'no-replay',
  'no-draw',
];

type Dict = Record<string, unknown>;

const isDict = (v: unknown): v is Dict => typeof v === 'object' && v !== null && !Array.isArray(v);
const isFinite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isInt = (v: unknown, min = 0, max = Number.MAX_SAFE_INTEGER): v is number =>
  Number.isInteger(v) && (v as number) >= min && (v as number) <= max;
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const hasControlChars = (s: string): boolean => {
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
};
const isText = (v: unknown, max: number): v is string =>
  typeof v === 'string' && v.length <= max && !hasControlChars(v);
const isId = (v: unknown): v is string => isText(v, MAX_ID_LENGTH) && v.length > 0;
/**
 * A seat id is opaque now (D-19 allows six of them), so the wire can only check its shape here.
 * Which ids a duel actually has is the duel's own business: `Duel` rejects anything that is not
 * one of its seats, which is stricter than the closed pair this used to allow.
 */
const isFighterId = (v: unknown): v is FighterId => isId(v);
const isSeatKind = (v: unknown): v is SeatKind => v === 'human' || v === 'ai' || v === 'none';
const isFights = (v: unknown): v is RoomFights =>
  v === 'arena' || v === 'card' || v === 'roll' || v === 'watch';

function parseRoomSeats(value: unknown): RoomSeat[] | undefined {
  if (!Array.isArray(value) || value.length > MAX_ROOM_MEMBERS) return undefined;
  const seats: RoomSeat[] = [];
  for (const raw of value) {
    if (!isDict(raw) || !(raw.kind === 'open' || raw.kind === 'ai' || raw.kind === 'none'))
      return undefined;
    const seat: RoomSeat = { kind: raw.kind };
    if (raw.depth !== undefined) {
      if (!isInt(raw.depth, 1, 4)) return undefined;
      seat.depth = raw.depth;
    }
    if (raw.duel !== undefined) {
      if (!isDuelLevel(raw.duel)) return undefined;
      seat.duel = raw.duel;
    }
    seats.push(seat);
  }
  return seats;
}
const isPlayerName = (v: unknown): v is string => isText(v, MAX_NAME_LENGTH);
const isClock = (v: unknown): v is number => CLOCK_CHOICES.includes(v as number);
const isWinner = (v: unknown): v is FactionId | 'draw' | null => v === null || isId(v);
const isCardRole = (v: unknown): v is CardRole =>
  v === 'pressure' || v === 'protection' || v === 'deception';
const isSupportCard = (v: unknown): v is SupportCardId =>
  typeof v === 'string' && /^(character:[a-z0-9-]+|element:(earth|water|air|fire))$/.test(v);

export function parseMove(value: unknown): Move | undefined {
  if (!isDict(value)) return undefined;
  const { type } = value;
  if (type === 'pass') return { type: 'pass' };
  if (type === 'draw') return { type: 'draw' };
  if (type === 'pass-battle') return { type: 'pass-battle' };
  if (type === 'join-battle')
    return isInt(value.pieceId, 1) ? { type: 'join-battle', pieceId: value.pieceId } : undefined;
  if (type === 'move') {
    if (isInt(value.pieceId, 1) && isInt(value.x) && isInt(value.y))
      return { type, pieceId: value.pieceId, x: value.x, y: value.y };
    return undefined;
  }
  if (type !== 'spell') return undefined;
  // Shift Time is the only spell with no caster: it is the player letting the round pass.
  if (value.spell === 'shift-time') return { type, spell: value.spell };
  const casterId = value.casterId;
  if (!isInt(casterId, 1)) return undefined;
  switch (value.spell) {
    case 'teleport':
      return isInt(value.pieceId, 1) && isInt(value.x) && isInt(value.y)
        ? { type, spell: value.spell, casterId, pieceId: value.pieceId, x: value.x, y: value.y }
        : undefined;
    case 'heal':
    case 'imprison':
    case 'curse':
    case 'freeze':
    case 'release':
      return isInt(value.pieceId, 1)
        ? { type, spell: value.spell, casterId, pieceId: value.pieceId }
        : undefined;
    case 'exchange':
      return isInt(value.pieceId, 1) && isInt(value.otherId, 1)
        ? { type, spell: value.spell, casterId, pieceId: value.pieceId, otherId: value.otherId }
        : undefined;
    case 'revive':
      return isId(value.character) && isInt(value.x) && isInt(value.y)
        ? { type, spell: value.spell, casterId, character: value.character, x: value.x, y: value.y }
        : undefined;
    // Summon was missing here altogether, so an online player could not cast it: the parser
    // dropped it to `undefined` and the server refused the move.
    case 'summon-elemental':
      return isInt(value.pieceId, 1) && isInt(value.x) && isInt(value.y)
        ? { type, spell: value.spell, casterId, pieceId: value.pieceId, x: value.x, y: value.y }
        : undefined;
    default:
      return undefined;
  }
}

/**
 * How far from straight ahead a yaw or pitch on the wire may be. Real ones stay within a half
 * turn: a player's facing and the arena AI's are folded into [−π, π], and the AI's pitch
 * overshoots the ±80° the simulation clamps it to by a few degrees at most. A tampered `1e15` is
 * refused here rather than handed to the server's and every client's simulation.
 */
const MAX_INPUT_ANGLE = 2 * Math.PI;
const isAngle = (v: unknown): v is number => isFinite(v) && Math.abs(v) <= MAX_INPUT_ANGLE;

/**
 * Movement axes are clamped so a tampered client cannot run faster than the rules allow, and
 * angles out of range are refused.
 */
export function parseFighterInput(value: unknown): FighterInput | undefined {
  if (!isDict(value)) return undefined;
  const { moveX, moveZ, speedScale, humanFriendlyAi, yaw, pitch, dash, attack, steer } = value;
  if (!isFinite(moveX) || !isFinite(moveZ) || !isAngle(yaw) || !isAngle(pitch)) return undefined;
  if (!isBool(dash) || !isBool(attack)) return undefined;
  const clamp = (v: number) => Math.max(-1, Math.min(1, v));
  if (speedScale !== undefined && !isFinite(speedScale)) return undefined;
  if (humanFriendlyAi !== undefined && !isBool(humanFriendlyAi)) return undefined;
  if (steer !== undefined && !isFinite(steer)) return undefined;
  return {
    moveX: clamp(moveX),
    moveZ: clamp(moveZ),
    ...(speedScale === undefined ? {} : { speedScale: Math.max(0.5, Math.min(1.25, speedScale)) }),
    ...(humanFriendlyAi === undefined ? {} : { humanFriendlyAi }),
    yaw,
    pitch,
    dash,
    attack,
    ...(steer === undefined ? {} : { steer: clamp(steer) }),
  };
}

function parseTickInputs(value: unknown): TickInputs | undefined {
  if (!isDict(value)) return undefined;
  const inputs: TickInputs = {};
  const entries = Object.entries(value);
  if (entries.length > MAX_SEATS) return undefined;
  for (const [key, raw] of entries) {
    if (!isFighterId(key)) return undefined;
    const input = parseFighterInput(raw);
    if (!input) return undefined;
    inputs[key] = input;
  }
  return inputs;
}

function parseTickBundle(value: unknown): TickBundle | undefined {
  if (!isDict(value) || !isInt(value.tick)) return undefined;
  const inputs = parseTickInputs(value.inputs);
  if (!inputs) return undefined;
  if (value.forfeit === undefined) return { tick: value.tick, inputs };
  return isFighterId(value.forfeit)
    ? { tick: value.tick, inputs, forfeit: value.forfeit }
    : undefined;
}

function parseTicks(value: unknown): TickBundle[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const ticks: TickBundle[] = [];
  for (const raw of value) {
    const bundle = parseTickBundle(raw);
    if (!bundle) return undefined;
    ticks.push(bundle);
  }
  return ticks;
}

/**
 * The damage a winner carries is a real number: a Card clash wounds by the expected damage and
 * a seeded spread, and every later fight adds to what a piece already carries. A check for whole
 * numbers refused every hosted Card result, and the table waited for it for ever.
 */
const MAX_DAMAGE = 1_000_000;

function parseBattleResult(value: unknown): BattleResult | undefined {
  if (!isDict(value)) return undefined;
  const { winnerId, winnerDamage } = value;
  if (winnerId !== null && !isInt(winnerId, 1)) return undefined;
  if (!isFinite(winnerDamage) || winnerDamage < 0 || winnerDamage > MAX_DAMAGE) return undefined;
  return { winnerId, winnerDamage };
}

function parseBattleStart(value: Dict): BattleStart | undefined {
  const { seed, attackerId, defenderId, humans } = value;
  if (!isInt(seed) || !isInt(attackerId, 1) || !isInt(defenderId, 1)) return undefined;
  if (!isDict(humans)) return undefined;
  const seats: Record<FighterId, boolean> = {};
  const entries = Object.entries(humans);
  if (entries.length === 0 || entries.length > MAX_SEATS) return undefined;
  for (const [seat, isHuman] of entries) {
    if (!isFighterId(seat) || !isBool(isHuman)) return undefined;
    seats[seat] = isHuman;
  }
  if (value.levels === undefined) return { seed, attackerId, defenderId, humans: seats };
  if (!isDict(value.levels)) return undefined;
  const levels: Partial<Record<FighterId, DuelLevel>> = {};
  const scaled = Object.entries(value.levels);
  if (scaled.length > MAX_SEATS) return undefined;
  for (const [seat, level] of scaled) {
    if (!isFighterId(seat) || !isDuelLevel(level)) return undefined;
    levels[seat] = level;
  }
  return { seed, attackerId, defenderId, humans: seats, levels };
}

function parseSetup(value: unknown): LobbySetup | undefined {
  if (!isDict(value) || !isId(value.boardId) || !isInt(value.players, 2, MAX_ROOM_MEMBERS))
    return undefined;
  if (!isClock(value.clockSeconds)) return undefined;
  const setup: LobbySetup = {
    boardId: value.boardId,
    players: value.players,
    clockSeconds: value.clockSeconds,
  };
  if (value.definition !== undefined) {
    try {
      setup.definition = validateGameDefinition(value.definition);
    } catch {
      return undefined;
    }
    if (setup.definition.id !== setup.boardId) return undefined;
  }
  if (value.seats !== undefined) {
    const seats = parseRoomSeats(value.seats);
    if (!seats) return undefined;
    setup.seats = seats;
  }
  if (value.fights !== undefined) {
    if (!isFights(value.fights)) return undefined;
    setup.fights = value.fights;
  }
  if (value.humanFriendlyAi !== undefined) {
    if (!isBool(value.humanFriendlyAi)) return undefined;
    setup.humanFriendlyAi = value.humanFriendlyAi;
  }
  if (value.engagement !== undefined) {
    if (value.engagement !== 'immediate' && value.engagement !== 'muster') return undefined;
    setup.engagement = value.engagement;
  }
  return setup;
}

function parseLobby(value: unknown): Lobby | undefined {
  if (!isDict(value) || !isId(value.code) || !isBool(value.started)) return undefined;
  const setup = parseSetup(value.setup);
  if (!setup || !Array.isArray(value.members)) return undefined;
  const members: LobbyMember[] = [];
  for (const m of value.members) {
    if (!isDict(m) || !isInt(m.seat, SPECTATOR_SEAT) || !isPlayerName(m.name)) return undefined;
    if (!isBool(m.host) || !isBool(m.connected)) return undefined;
    members.push({ seat: m.seat, name: m.name, host: m.host, connected: m.connected });
  }
  return { code: value.code, setup, members, started: value.started };
}

function parseHistory(value: unknown): HistoryEntry[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const history: HistoryEntry[] = [];
  for (const raw of value) {
    if (!isDict(raw)) return undefined;
    if (raw.kind === 'move') {
      const move = parseMove(raw.move);
      if (!move || !isInt(raw.by)) return undefined;
      history.push({ kind: 'move', move, by: raw.by });
    } else if (raw.kind === 'battle') {
      const result = parseBattleResult(raw.result);
      if (!result || !isInt(raw.rng)) return undefined;
      if (raw.card === undefined) history.push({ kind: 'battle', result, rng: raw.rng });
      else {
        const parsed = parseCardScores(raw.card);
        if (!parsed) return undefined;
        history.push({ kind: 'battle', result, rng: raw.rng, card: parsed });
      }
    } else return undefined;
  }
  return history;
}

function parseSeatsAndNames(
  seats: unknown,
  names: unknown,
): { seats: SeatKind[]; names: string[] } | undefined {
  if (!Array.isArray(seats) || !Array.isArray(names)) return undefined;
  if (!seats.every(isSeatKind) || !names.every(isPlayerName)) return undefined;
  if (seats.length !== names.length) return undefined;
  return { seats, names };
}

function parseReplaySummary(value: unknown): ReplaySummary | undefined {
  if (!isDict(value) || !isId(value.id) || !isFinite(value.finishedAt)) return undefined;
  const setup = parseSetup(value.setup);
  const seating = parseSeatsAndNames(value.seats, value.names);
  if (!setup || !seating || !isInt(value.moves) || !isInt(value.duels)) return undefined;
  if (!isWinner(value.winner)) return undefined;
  return {
    id: value.id,
    finishedAt: value.finishedAt,
    setup,
    ...seating,
    moves: value.moves,
    duels: value.duels,
    winner: value.winner,
  };
}

function parseReplay(value: unknown): Replay | undefined {
  if (!isDict(value) || !isInt(value.seed) || !Array.isArray(value.duels)) return undefined;
  const summary = parseReplaySummary(value.summary);
  const history = parseHistory(value.history);
  if (!summary || !history) return undefined;
  const duels: ReplayDuel[] = [];
  for (const raw of value.duels) {
    if (!isDict(raw) || !isInt(raw.index)) return undefined;
    const start = parseBattleStart(raw);
    const ticks = parseTicks(raw.ticks);
    if (!start || !ticks) return undefined;
    duels.push({ ...start, index: raw.index, ticks });
  }
  return { summary, seed: value.seed, history, duels };
}

/** Turns whatever came over the wire into a client message, or nothing when it is malformed. */
export function parseClientMessage(value: unknown): ClientMessage | undefined {
  if (!isDict(value)) return undefined;
  switch (value.t) {
    case 'create': {
      const { boardId, players, seat, name, clockSeconds } = value;
      if (!isId(boardId) || !isInt(players, 2, MAX_ROOM_MEMBERS)) return undefined;
      if (!isInt(seat, 0, MAX_ROOM_MEMBERS - 1) || !isPlayerName(name)) return undefined;
      if (!isClock(clockSeconds)) return undefined;
      const message: Extract<ClientMessage, { t: 'create' }> = {
        t: 'create',
        boardId,
        players,
        seat,
        name,
        clockSeconds,
      };
      if (value.definition !== undefined) {
        try {
          message.definition = validateGameDefinition(value.definition);
        } catch {
          return undefined;
        }
        if (message.definition.id !== boardId) return undefined;
      }
      if (value.seats !== undefined) {
        const seats = parseRoomSeats(value.seats);
        if (!seats) return undefined;
        message.seats = seats;
      }
      if (value.fights !== undefined) {
        if (!isFights(value.fights)) return undefined;
        message.fights = value.fights;
      }
      if (value.humanFriendlyAi !== undefined) {
        if (!isBool(value.humanFriendlyAi)) return undefined;
        message.humanFriendlyAi = value.humanFriendlyAi;
      }
      if (value.engagement !== undefined) {
        if (value.engagement !== 'immediate' && value.engagement !== 'muster') return undefined;
        message.engagement = value.engagement;
      }
      return message;
    }
    case 'join': {
      const { code, name, watch } = value;
      if (typeof code !== 'string' || !isValidJoinCode(code) || !isPlayerName(name))
        return undefined;
      if (watch !== undefined && !isBool(watch)) return undefined;
      return watch === undefined ? { t: 'join', code, name } : { t: 'join', code, name, watch };
    }
    case 'leave':
    case 'start':
    case 'forfeit':
    case 'reclaim':
    case 'replays':
    case 'cardContinue':
    case 'offerDraw':
      return { t: value.t };
    case 'answerDraw':
      return isBool(value.accept) ? { t: 'answerDraw', accept: value.accept } : undefined;
    case 'move': {
      const move = parseMove(value.move);
      return move ? { t: 'move', move } : undefined;
    }
    case 'cardChoice':
      if (value.cardId !== null && !isSupportCard(value.cardId)) return undefined;
      if (value.role !== undefined && !isCardRole(value.role)) return undefined;
      return value.role === undefined
        ? { t: 'cardChoice', cardId: value.cardId }
        : { t: 'cardChoice', cardId: value.cardId, role: value.role };
    case 'input': {
      const input = parseFighterInput(value.input);
      return input && isInt(value.tick) ? { t: 'input', tick: value.tick, input } : undefined;
    }
    case 'resume': {
      const { token, standIn } = value;
      if (!isText(token, MAX_TOKEN_LENGTH) || token.length === 0) return undefined;
      if (standIn !== undefined && !isBool(standIn)) return undefined;
      return standIn ? { t: 'resume', token, standIn } : { t: 'resume', token };
    }
    case 'ping':
      return isFinite(value.sent) ? { t: 'ping', sent: value.sent } : undefined;
    case 'replay':
      return isId(value.id) ? { t: 'replay', id: value.id } : undefined;
    default:
      return undefined;
  }
}

/** The client's side of the same check: a server answer that does not parse is dropped. */
export function parseServerMessage(value: unknown): ServerMessage | undefined {
  if (!isDict(value)) return undefined;
  switch (value.t) {
    case 'hello':
      return isText(value.version, MAX_ID_LENGTH) &&
        isInt(value.protocol) &&
        isFinite(value.serverTime) &&
        (value.content === undefined || isText(value.content, MAX_ID_LENGTH))
        ? {
            t: 'hello',
            version: value.version,
            protocol: value.protocol,
            serverTime: value.serverTime,
            ...(value.content === undefined ? {} : { content: value.content }),
          }
        : undefined;
    case 'pong':
      return isFinite(value.sent) && isFinite(value.serverTime)
        ? { t: 'pong', sent: value.sent, serverTime: value.serverTime }
        : undefined;
    case 'created':
    case 'joined': {
      const lobby = parseLobby(value.lobby);
      if (!lobby || !isId(value.code) || !isInt(value.seat, SPECTATOR_SEAT)) return undefined;
      if (!isText(value.token, MAX_TOKEN_LENGTH)) return undefined;
      return { t: value.t, code: value.code, seat: value.seat, token: value.token, lobby };
    }
    case 'lobby': {
      const lobby = parseLobby(value.lobby);
      return lobby ? { t: 'lobby', lobby } : undefined;
    }
    case 'started': {
      const setup = parseSetup(value.setup);
      const seating = parseSeatsAndNames(value.seats, value.names);
      if (!setup || !seating || !isInt(value.seed)) return undefined;
      if (value.playing === undefined) return { t: 'started', seed: value.seed, setup, ...seating };
      if (!Array.isArray(value.playing) || !value.playing.every(isId)) return undefined;
      return { t: 'started', seed: value.seed, setup, ...seating, playing: value.playing };
    }
    case 'sync': {
      const history = parseHistory(value.history);
      if (!history) return undefined;
      const standIns = value.standIns;
      if (
        standIns !== undefined &&
        !(
          Array.isArray(standIns) &&
          standIns.length <= MAX_ROOM_MEMBERS &&
          standIns.every((seat) => isInt(seat, 0, MAX_ROOM_MEMBERS - 1))
        )
      )
        return undefined;
      const paused = value.paused;
      if (
        paused !== undefined &&
        !(
          Array.isArray(paused) &&
          paused.length <= MAX_ROOM_MEMBERS &&
          paused.every(
            (entry) =>
              isDict(entry) &&
              isInt(entry.seat, 0, MAX_ROOM_MEMBERS - 1) &&
              isInt(entry.secondsLeft),
          )
        )
      )
        return undefined;
      const message: Extract<ServerMessage, { t: 'sync' }> = { t: 'sync', history };
      if (value.battle !== undefined) {
        if (!isDict(value.battle)) return undefined;
        const battle = parseBattleStart(value.battle);
        const ticks = parseTicks(value.battle.ticks);
        if (!battle || !ticks) return undefined;
        message.battle = { ...battle, ticks };
      }
      if (standIns?.length) message.standIns = standIns as number[];
      if (paused?.length)
        message.paused = (paused as Dict[]).map((entry) => ({
          seat: entry.seat as number,
          secondsLeft: entry.secondsLeft as number,
        }));
      return message;
    }
    case 'moved': {
      const move = parseMove(value.move);
      if (!move || !isInt(value.by)) return undefined;
      if (value.timeout !== undefined && !isBool(value.timeout)) return undefined;
      return value.timeout
        ? { t: 'moved', move, by: value.by, timeout: true }
        : { t: 'moved', move, by: value.by };
    }
    case 'clock':
      return isInt(value.seat) && isInt(value.secondsLeft)
        ? { t: 'clock', seat: value.seat, secondsLeft: value.secondsLeft }
        : undefined;
    case 'battle': {
      const battle = parseBattleStart(value);
      return battle ? { t: 'battle', ...battle } : undefined;
    }
    case 'tick': {
      const bundle = parseTickBundle(value);
      return bundle ? { t: 'tick', ...bundle } : undefined;
    }
    case 'battleEnded': {
      const result = parseBattleResult(value.result);
      return result && isInt(value.rng) ? { t: 'battleEnded', result, rng: value.rng } : undefined;
    }
    case 'cardPrompt': {
      if (!isId(value.faction) || !Array.isArray(value.participants) || !Array.isArray(value.hand))
        return undefined;
      const participants = value.participants.map((raw) => {
        if (
          !isDict(raw) ||
          !isInt(raw.pieceId, 1) ||
          !isId(raw.faction) ||
          !isId(raw.character) ||
          !isText(raw.name, 100) ||
          !isFinite(raw.main) ||
          (raw.level !== undefined && !isDuelLevel(raw.level))
        )
          return null;
        return {
          pieceId: raw.pieceId,
          faction: raw.faction,
          character: raw.character,
          name: raw.name,
          main: raw.main,
          ...(raw.level !== undefined ? { level: raw.level as DuelLevel } : {}),
        };
      });
      const hand = value.hand.map((raw) => {
        if (
          !isDict(raw) ||
          !isSupportCard(raw.id) ||
          !isText(raw.name, 100) ||
          !isCardRole(raw.role) ||
          !isBool(raw.water) ||
          !isText(raw.fit, 160)
        )
          return null;
        return { id: raw.id, name: raw.name, role: raw.role, water: raw.water, fit: raw.fit };
      });
      if (participants.some((p) => !p) || hand.some((card) => !card)) return undefined;
      const { secondsLeft } = value;
      if (secondsLeft !== undefined && !isInt(secondsLeft, 0, CARD_CHOICE_SECONDS))
        return undefined;
      return {
        t: 'cardPrompt',
        faction: value.faction,
        participants: participants as NonNullable<(typeof participants)[number]>[],
        hand: hand as NonNullable<(typeof hand)[number]>[],
        ...(secondsLeft !== undefined ? { secondsLeft } : {}),
      };
    }
    case 'cardReveal': {
      const scores = parseCardScores(value.scores);
      const { holdSeconds } = value;
      if (!scores || (holdSeconds !== undefined && !isInt(holdSeconds, 1, CARD_REVEAL_SECONDS)))
        return undefined;
      return holdSeconds === undefined
        ? { t: 'cardReveal', scores }
        : { t: 'cardReveal', scores, holdSeconds };
    }
    case 'paused':
      return isInt(value.seat) && isFinite(value.secondsLeft)
        ? { t: 'paused', seat: value.seat, secondsLeft: value.secondsLeft }
        : undefined;
    case 'resumed':
      return isInt(value.seat) ? { t: 'resumed', seat: value.seat } : undefined;
    case 'seatChanged': {
      const { seat, kind, standIn } = value;
      if (!isInt(seat) || !isSeatKind(kind)) return undefined;
      if (standIn !== undefined && !isBool(standIn)) return undefined;
      return standIn && kind === 'ai'
        ? { t: 'seatChanged', seat, kind, standIn }
        : { t: 'seatChanged', seat, kind };
    }
    case 'drawOffer': {
      const { seat, waiting } = value;
      if (!isInt(seat) || !Array.isArray(waiting) || waiting.length > MAX_ROOM_MEMBERS)
        return undefined;
      if (!waiting.every((w) => isInt(w))) return undefined;
      return { t: 'drawOffer', seat, waiting: waiting as number[] };
    }
    case 'drawDeclined':
      return value.by === null || isInt(value.by)
        ? { t: 'drawDeclined', by: value.by as number | null }
        : undefined;
    case 'ended': {
      const { reason, winner } = value;
      if (reason !== 'finished' && reason !== 'abandoned' && reason !== 'expired') return undefined;
      if (!isWinner(winner)) return undefined;
      return { t: 'ended', reason, winner };
    }
    case 'replays': {
      if (!Array.isArray(value.replays)) return undefined;
      const replays: ReplaySummary[] = [];
      for (const raw of value.replays) {
        const summary = parseReplaySummary(raw);
        if (!summary) return undefined;
        replays.push(summary);
      }
      return { t: 'replays', replays };
    }
    case 'replay': {
      const replay = parseReplay(value.replay);
      return replay ? { t: 'replay', replay } : undefined;
    }
    case 'error':
      return ERROR_CODES.includes(value.code as ErrorCode) && isText(value.message, 200)
        ? { t: 'error', code: value.code as ErrorCode, message: value.message }
        : undefined;
    default:
      return undefined;
  }
}

function parseCardScores(value: unknown): CardScore[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const scores: CardScore[] = [];
  for (const raw of value) {
    if (
      !isDict(raw) ||
      !isId(raw.faction) ||
      !isInt(raw.pieceId, 1) ||
      !isFinite(raw.main) ||
      !isFinite(raw.support) ||
      !isFinite(raw.total)
    )
      return undefined;
    if (raw.cardId !== null && !isSupportCard(raw.cardId)) return undefined;
    if (raw.role !== null && !isCardRole(raw.role)) return undefined;
    if (!isBool(raw.fit) || !isBool(raw.counter)) return undefined;
    if (
      raw.probability !== undefined &&
      (!isFinite(raw.probability) || raw.probability < 0 || raw.probability > 1)
    )
      return undefined;
    if (raw.source !== undefined && raw.source !== 'measured' && raw.source !== 'estimated')
      return undefined;
    if (raw.won !== undefined && !isBool(raw.won)) return undefined;
    if (raw.level !== undefined && !isDuelLevel(raw.level)) return undefined;
    scores.push({
      faction: raw.faction,
      pieceId: raw.pieceId,
      main: raw.main,
      support: raw.support,
      total: raw.total,
      cardId: raw.cardId,
      role: raw.role,
      fit: raw.fit,
      counter: raw.counter,
      ...(raw.probability !== undefined ? { probability: raw.probability as number } : {}),
      ...(raw.source !== undefined ? { source: raw.source as 'measured' | 'estimated' } : {}),
      ...(raw.won !== undefined ? { won: raw.won as boolean } : {}),
      ...(raw.level !== undefined ? { level: raw.level as DuelLevel } : {}),
    });
  }
  return scores;
}

/** JSON text → message; anything that is not valid JSON or not a valid message is dropped. */
export function decodeClientMessage(raw: string): ClientMessage | undefined {
  return parseClientMessage(safeJson(raw));
}

export function decodeServerMessage(raw: string): ServerMessage | undefined {
  return parseServerMessage(safeJson(raw));
}

function safeJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return undefined;
  }
}
